import * as React from 'react';
import { useDispatch } from 'react-redux';
import { SortHelper } from '../../SortHelper';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../interfaces/Customer';
import { customersActionCreators } from '../../store/customers';
import { dataActionCreators, Project } from '../../store/Data';
import { AddIcon, ArrowClockwiseIcon, EditIcon, FilterIcon, GroupIcon, TrashCanIcon, useEditorStyles, useTabStyles } from '../Styles/Editor';
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button, InputOnChangeData, Popover, PopoverSurface, PopoverTrigger, SearchBox, SearchBoxChangeEvent, Skeleton, SkeletonItem, Table, TableBody, TableCell, TableCellActions, TableCellLayout, TableHeader, TableHeaderCell, TableRow, Tag, Text } from '@fluentui/react-components';
import { CustomerDropdown } from '../EditorHelpers/CustomerDropdown';
import { useCustomers, useProjects, useProjectsAssigments } from '../Tools/ObjectLoader';

export const ProjectsTab = () => {
    const { customers } = useCustomers();
    const { projects } = useProjects();
    const { projectsAssigments } = useProjectsAssigments();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [filter, setFilter] = React.useState<string | null>(null);
    const [filterCustomer, setFilterCustomer] = React.useState<Customer | undefined>(undefined);
    const [filteredProjects, setFilteredProjects] = React.useState<Project[] | undefined>(undefined);
    const styles = useEditorStyles();
    const stylesTab = useTabStyles();

    React.useEffect(() => {
        if (projects != undefined) {
            let filtered = projects;
            // Apply filter if any
            if (filterCustomer) {
                filtered = filtered
                    .filter(p => {
                        if (p.customerid === filterCustomer.customerid) {
                            return true;
                        }
                        return false;
                    });
            }
            if (filter && filter.length != 0) {
                filtered = filtered
                    .filter(p => p.displayName && p.displayName.toLowerCase().includes(filter.toLowerCase()));
            }
            setFilteredProjects(filtered);
        }
    }, [projects, filter, filterCustomer]);

    const MapSkeleton = (i: number) => <TableRow key={i}>
        <TableCell>
            <Skeleton animation="pulse">
                <SkeletonItem className={stylesTab.width15} />
            </Skeleton>
        </TableCell>
        <TableCell>
            <Skeleton animation="pulse">
                <SkeletonItem className={stylesTab.width15} />
            </Skeleton>
        </TableCell>
        <TableCell>
            <Skeleton animation="pulse">
                <SkeletonItem className={stylesTab.width15} />
            </Skeleton>
        </TableCell>
        <TableCell>
            <Skeleton animation="pulse">
                <SkeletonItem className={stylesTab.width15} />
            </Skeleton>
        </TableCell>
    </TableRow>;

    const mapProject = (p: Project): JSX.Element => {
        const c = customers ? customers.find(e => e.customerid === p.customerid) : undefined;
        const pa = projectsAssigments ? projectsAssigments.find(paTemp => p.projectid === paTemp.projectid) : undefined;
        return <TableRow key={p.projectid}>
            <TableCell>
                {c && c.displayName
                    ? <Button appearance="transparent" className={stylesTab.TestButton}
                        onClick={() => dispatch(customersActionCreators.requestCustomerQuickView(c))}>
                        {c.displayName ? c.displayName : "?"}
                    </Button>
                    : <Text>"?"</Text>
                }
            </TableCell>
            <TableCell>
                <TableCellLayout>
                    <Button appearance="transparent" className={stylesTab.TestButton}
                        onClick={() => dispatch(dataActionCreators.requestProjectQuickView(p))} >
                        {p.displayName ? p.displayName : "?"}
                    </Button>
                </TableCellLayout>
                <TableCellActions>
                    <Button
                        icon={<EditIcon />}
                        appearance="subtle"
                        aria-label={t('Edit')}
                        onClick={() => dispatch(dataActionCreators.requestProjectEdition(p))}
                    />
                    <Button
                        icon={<GroupIcon />}
                        appearance="subtle"
                        aria-label={t('People')}
                        onClick={() => dispatch(dataActionCreators.requestProjectContactsEdition(p))}
                    />
                    {pa === undefined &&
                        <Button
                            icon={<TrashCanIcon />}
                            appearance="subtle"
                            aria-label="Delete"
                            onClick={() => dispatch(dataActionCreators.requestProjectDeletion(p))}
                        />
                    }
                </TableCellActions>
            </TableCell>
            <TableCell>{(p.dateDebut ? new Date(p.dateDebut).toLocaleDateString() : "-") + " → " + (p.dateFin ? new Date(p.dateFin).toLocaleDateString() : "-")}</TableCell>
        </TableRow>;
    };

    const columns = [
        { columnKey: "Customers", label: t("Customer"), className: stylesTab.width35 },
        { columnKey: "Project", label: t('Project'), className: stylesTab.width35 },
        { columnKey: "PeriodSlice", label: t('PeriodSlice'), className: stylesTab.width15 },
    ];

    return <div className={stylesTab.rootSub}>
        <div className={stylesTab.MainBar}>
            <Popover positioning="below-start">
                <PopoverTrigger>
                    <Button icon={<FilterIcon />} shape="circular" title="Filter" />
                </PopoverTrigger>
                <PopoverSurface>
                    <div className={stylesTab.List}>
                        {t('Customer')}
                        <CustomerDropdown currentCustomer={filterCustomer ?? undefined}
                            customers={customers ?? []}
                            setCustomer={setFilterCustomer} />
                    </div>
                </PopoverSurface>
            </Popover>
            {filterCustomer &&
                <Tag
                    dismissible
                    dismissIcon={{ "aria-label": "remove" }}
                    size="small"
                    onClick={() => { setFilterCustomer(undefined); }}
                    appearance="outline"
                >
                    {t('Customer') + " = " + filterCustomer.displayName}
                </Tag>
            }
            <SearchBox
                placeholder={t('Contains')}
                onChange={(e: SearchBoxChangeEvent, data: InputOnChangeData) => { setFilter(data.value); }}
                dismiss={{ onClick: () => { setFilter(null); } }}
                className={styles.fluid}
            />
            <div className={stylesTab.MarginLeftAuto}>
                <Button icon={<AddIcon />} shape="circular" title={t('Add')}
                    onClick={() => {
                        const p: Project = {
                            projectid: null,
                            customerid: null,
                            displayName: "",
                            actif: true,
                            prixForfaitaire: null,
                            dateDebut: null,
                            dateFin: null,
                            description: null,
                            contacts: null,
                        };
                        dispatch(dataActionCreators.requestProjectEdition(p));
                    }} />
                <Button icon={<ArrowClockwiseIcon />} shape="circular" title="Reload"
                    onClick={() => {
                        dispatch(dataActionCreators.ReloadProjects());
                    }} />
            </div>
        </div>
        <Accordion multiple collapsible defaultOpenItems="1">
            <AccordionItem value="1">
                <AccordionHeader><h2>{t('Ongoing')}</h2></AccordionHeader>
                <AccordionPanel>
                    <Table arial-label="Default table">
                        <TableHeader>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableHeaderCell key={column.columnKey} className={column.className}>
                                        {column.label}
                                    </TableHeaderCell>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredProjects === undefined
                                ? [...Array(10)].map((_, index) => MapSkeleton(index))
                                : filteredProjects.filter(t => (t.dateFin === null || Date.now() <= new Date(t.dateFin).getTime()))
                                    .sort((a, b) => SortHelper.ProjectByCustomerThenDisplayName(customers, a, b))
                                    .map(p => mapProject(p)
                                    )}
                        </TableBody>
                    </Table>
                </AccordionPanel>
            </AccordionItem>
            <AccordionItem value="2">
                <AccordionHeader><h2>{t('Obsolete')}</h2></AccordionHeader>
                <AccordionPanel>
                    <Table arial-label="Default table">
                        <TableHeader>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableHeaderCell key={column.columnKey} className={column.className}>
                                        {column.label}
                                    </TableHeaderCell>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredProjects === undefined
                                ? [...Array(10)].map((_, index) => MapSkeleton(index))
                                : filteredProjects.filter(t => (t.dateFin !== null && Date.now() > new Date(t.dateFin).getTime()))
                                    .sort((a, b) => SortHelper.ProjectByCustomerThenDisplayName(customers, a, b))
                                    .map(p => mapProject(p)
                                    )}
                        </TableBody>
                    </Table>
                </AccordionPanel>
            </AccordionItem>
        </Accordion>
    </div>;
};